// Six-box PIN entry (auth model: 6-digit PIN). Accepts Arabic-Indic or
// Latin digits; the value is always ASCII. One real input drives the boxes so
// paste, autofill and screen readers work.

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { normalizeDigits } from '@/lib/digits';

export const PIN_LENGTH = 6;

export interface PinInputProps {
  value: string;
  onChange: (pin: string) => void;
  onComplete?: (pin: string) => void;
  label: string;
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
}

export const PinInput = forwardRef<HTMLInputElement, PinInputProps>(function PinInput(
  { value, onChange, onComplete, label, disabled, autoFocus, invalid },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  // Typing feedback (owner 2026-10-14): the box being written into carries a
  // blinking caret, a digit pops into its box, and a wrong PIN shakes the row.
  const [focused, setFocused] = useState(false);
  const [shake, setShake] = useState(0);
  const prevInvalid = useRef(false);
  useEffect(() => {
    if (invalid && !prevInvalid.current) setShake((n) => n + 1);
    prevInvalid.current = !!invalid;
  }, [invalid]);
  const active = focused && !disabled && value.length < PIN_LENGTH ? value.length : -1;
  return (
    <div
      className="e-pin"
      onClick={() => inner.current?.focus()}
      data-invalid={invalid ? 'true' : undefined}
      data-shake={shake > 0 ? String(shake) : undefined}
      data-complete={value.length === PIN_LENGTH ? 'true' : undefined}
    >
      <input
        ref={inner}
        className="e-pin-input"
        inputMode="numeric"
        autoComplete="current-password"
        type="password"
        aria-label={label}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        maxLength={PIN_LENGTH * 2}
        onChange={(e) => {
          const pin = normalizeDigits(e.target.value).slice(0, PIN_LENGTH);
          onChange(pin);
          if (pin.length === PIN_LENGTH) onComplete?.(pin);
        }}
      />
      {Array.from({ length: PIN_LENGTH }, (_, i) => (
        <span
          key={i}
          className="e-pin-box"
          data-filled={i < value.length ? 'true' : 'false'}
          data-active={i === active ? 'true' : undefined}
          aria-hidden="true"
        >
          {i < value.length ? <span className="e-pin-dot" key={`d${i}`}>•</span> : i === active ? <span className="e-pin-caret" /> : null}
        </span>
      ))}
    </div>
  );
});
